import { useEffect, useMemo, useRef, useState } from 'react';
import { httpInterceptor } from '../../API/interceptor.jsx';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { KeyConstant } from '../../utils/constants/keys.constants';

import './style.scss';
import {ATTENDANCE_WINDOW_CLOSED, ATTENDANCE_CLOSED_MESSAGE} from '../../utils/digitalAttendance';
import {
  addBeneficiary,
  getBeneficiaryById,
  getMeetingsByActivityGroup,
  getMeetingsByGroup,
  getMunicipalities,
  getNeighborhoods,
  getPublicAttendanceContext,
  getPublicAttendanceNeighborhoods,
  getZones,
  registerPublicAttendance,
  saveBatchMeetingAttendance
} from '../../API/apiService';

import ModalContainer from '../../components/modalContainer';
import Select from '../../components/select';
import SignatureCanvas from '../../components/signatureCanvas';
import { toast } from 'react-toastify';
import { useUserName } from '../../contexts/userName';
import {
  buildProtectedImageUrl,
  fetchWithAuth
} from '../../utils/secureMedia';
import { usesCompactAttendanceCharacterization } from './characterizationMode';
import {ensureIdentificationTypeOptions} from '../../utils/identificationTypes';
import {
  CONTROL_SOCIAL_ORGANIZATION_OPTIONS,
  getControlSocialOrganizationValue,
  isOtherControlSocialOrganization,
  resolveStoredControlSocialOrganization
} from './controlSocialOrganization';

const COP_AGE_RANGE_OPTIONS = [
  { id: 'PRIMERA_INFANCIA_0_5', value: 'Primera infancia 0-5' },
  { id: 'NINEZ_ADOLESCENCIA_6_17', value: 'Ninez-adolescencia 6-17' },
  { id: 'JUVENTUD_18_28', value: 'Juventud 18-28' },
  { id: 'ADULTO_29_54', value: 'Adulto/a 29-54' },
  { id: 'ADULTO_MAYOR_55_MAS', value: 'Adulto mayor 55 o mas' }
];

const COP_DIFFERENTIAL_OPTIONS = [
  { id: 'NINGUNO', value: 'Ninguno' },
  { id: 'AFRODESCENDIENTE', value: 'Afrodescendiente', ethnicCode: 'ET_02' },
  { id: 'INDIGENA', value: 'Indigena', ethnicCode: 'ET_04' },
  { id: 'ROM', value: 'ROM', ethnicCode: 'ET_05' },
  { id: 'LGBTIQ', value: 'LGBTIQ+' },
  { id: 'SITUACION_CALLE', value: 'Situacion de calle' },
  { id: 'CAMPESINO', value: 'Campesino' },
  { id: 'DISCAPACIDAD', value: 'Discapacidad' }
];

const COP_ORGANIZATION_MAX_LENGTH = 65535;

const SCHOOL_ETHNIC_GROUP_OPTIONS = [
  { id: 'ET_TULE', value: 'Tule (Kuna)' },
  { id: 'ET_EMBERA', value: 'Emberá (Dobidá, Eyabidá, Katío y Chamí)' },
  { id: 'ET_SENU', value: 'Senú' },
  { id: 'ET_KAMSA', value: 'Kamsá' },
  { id: 'ET_NASA', value: 'Nasa (Paez)' },
  { id: 'ET_PIJAO', value: 'Pijaos' },
  { id: 'ET_NEGRO', value: 'Negro' },
  { id: 'ET_AFRO', value: 'AfroColombiano - Afrodescendiente' },
  { id: 'ET_PALENQ', value: 'Palenquero' },
  { id: 'ET_RAIZAL', value: 'Raizal' },
  { id: 'ET_ROM', value: 'Rom - Gitano' },
  { id: 'ET_NINGUNO', value: 'Ninguno' }
];

const SCHOOL_VICTIM_POPULATION_OPTIONS = [
  { id: 'Si', value: 'Si' },
  { id: 'No', value: 'No' }
];

function MultiChoice({ field, options, selectedIds, selectedValues, onChange }) {
  const idPieces = String(selectedIds || '').split('|').map(normalizeText);
  const valuePieces = String(selectedValues || '').split('|').map(normalizeText);
  const selectedOptions = options.filter(option =>
    idPieces.includes(normalizeText(option.id)) ||
    valuePieces.includes(normalizeText(option.value)) ||
    (isOtherOptionValue(option.value) && valuePieces.some(isOtherOptionValue))
  );

  function toggleOption(option) {
    const isSelected = selectedOptions.some(
      selected => String(selected.id) === String(option.id)
    );
    const isExclusive = ['DIS_99', 'RIESGO_00'].includes(
      String(option.code || '').toUpperCase()
    );
    let nextOptions = isSelected
      ? selectedOptions.filter(selected => String(selected.id) !== String(option.id))
      : [...selectedOptions, option];

    if (!isSelected && isExclusive) {
      nextOptions = [option];
    } else if (!isSelected) {
      nextOptions = nextOptions.filter(selected =>
        !['DIS_99', 'RIESGO_00'].includes(String(selected.code || '').toUpperCase())
      );
    }

    onChange(
      nextOptions[0]?.id || '',
      nextOptions.map(selected => selected.value).join(' | ')
    );
  }

  return (
    <details className='multi-choice'>
      <summary className='multi-choice__summary'>
        <span>
          {selectedOptions.length > 0
            ? `${selectedOptions.length} opcion${selectedOptions.length === 1 ? '' : 'es'} seleccionada${selectedOptions.length === 1 ? '' : 's'}`
            : 'Seleccione una o varias opciones'}
        </span>
        <span className='material-symbols-rounded' aria-hidden='true'>
          expand_more
        </span>
      </summary>
      <div className='cop-options multi-choice__options' role='group'>
        {options.map(option => {
          const inputId = `${field}-${option.id}`;
          const checked = selectedOptions.some(
            selected => String(selected.id) === String(option.id)
          );

          return (
            <label className='cop-options__item' key={option.id} htmlFor={inputId}>
              <input
                id={inputId}
                className='cop-options__input'
                type='checkbox'
                checked={checked}
                onChange={() => toggleOption(option)}
              />
              <span className='cop-options__box' aria-hidden='true'></span>
              <span className='cop-options__text'>{option.value}</span>
            </label>
          );
        })}
      </div>
    </details>
  );
}

function normalizeText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function isOtherOptionValue(value) {
  const normalized = normalizeText(value);
  return normalized === 'otro' || normalized.startsWith('otro:');
}

function prioritizeNoneOption(options) {
  return [...options].sort((left, right) => {
    const leftIsNone = normalizeText(left?.value) === 'ninguno';
    const rightIsNone = normalizeText(right?.value) === 'ninguno';
    return Number(rightIsNone) - Number(leftIsNone);
  });
}

function resolveYesNo(value) {
  const normalized = normalizeText(value);
  if (value === true || normalized === 'si' || normalized === 'true') {
    return true;
  }
  if (value === false || normalized === 'no' || normalized === 'false') {
    return false;
  }
  return null;
}

function parseCopDifferentialFocus(value) {
  if (Array.isArray(value)) {
    return value;
  }

  const pieces = String(value || '')
    .split(/[|,]/)
    .map(piece => piece.trim())
    .filter(Boolean);

  return pieces
    .map(piece => {
      const normalizedPiece = normalizeText(piece);
      const option = COP_DIFFERENTIAL_OPTIONS.find(
        item =>
          normalizeText(item.id) === normalizedPiece ||
          normalizeText(item.value) === normalizedPiece
      );
      return option?.id;
    })
    .filter(Boolean);
}

function getCopAgeRangeFromAge(age) {
  const numericAge = Number(age);
  if (!Number.isFinite(numericAge) || numericAge < 0) {
    return '';
  }

  if (numericAge <= 5) return 'PRIMERA_INFANCIA_0_5';
  if (numericAge <= 17) return 'NINEZ_ADOLESCENCIA_6_17';
  if (numericAge <= 28) return 'JUVENTUD_18_28';
  if (numericAge <= 54) return 'ADULTO_29_54';
  return 'ADULTO_MAYOR_55_MAS';
}

function buildCopDifferentialIdsFromBeneficiary(beneficiary) {
  const inferred = [];
  const ethnicValue = normalizeText(
    beneficiary?.ethnic_group_value ?? beneficiary?.ethnicGroupValue
  );
  const riskValue = normalizeText(
    beneficiary?.risk_factor_value ?? beneficiary?.riskFactorValue
  );
  const disabilityValue = normalizeText(
    beneficiary?.disability_value ??
      beneficiary?.disabilityValue ??
      beneficiary?.disability
  );

  if (ethnicValue.includes('afro')) inferred.push('AFRODESCENDIENTE');
  if (ethnicValue.includes('indigena')) inferred.push('INDIGENA');
  if (ethnicValue.includes('rom')) inferred.push('ROM');
  if (resolveYesNo(beneficiary?.lgbtiq) === true) inferred.push('LGBTIQ');
  if (riskValue.includes('calle')) inferred.push('SITUACION_CALLE');
  if (resolveYesNo(beneficiary?.farmer) === true) inferred.push('CAMPESINO');
  if (
    disabilityValue &&
    !disabilityValue.includes('no tiene') &&
    disabilityValue !== 'ninguna'
  ) {
    inferred.push('DISCAPACIDAD');
  }

  return inferred;
}

export default function Characterization() {
  const { userId, userRole } = useUserName();
  // STATES  por characterization //
  const [personalAge, setPersonalAge] = useState('');
  const [controlSocialOrganizationOther, setControlSocialOrganizationOther] =
    useState('');
  const [consentFile, setConsentFile] = useState(null);
  const [consentFileName, setConsentFileName] = useState('');
  const [storedConsentFileName, setStoredConsentFileName] = useState('');
  const [consentImageId, setConsentImageId] = useState(null);
  const [consentPreviewUrl, setConsentPreviewUrl] = useState('');
  const [isDownloadingStoredConsent, setIsDownloadingStoredConsent] =
    useState(false);
  const [personalInfo, setPersonalInfo] = useState({
    birthdate: '',
    fullName: '',
    gender: '',
    idNumber: '',
    idType: '',
    address: '',
    companyEntity: '',
    ageRange: '',
    municipality: '',
    municipalityName: '',
    zone: '',
    neighborhood: '',
    neighborhoodName: '',
    cellPhone: '',
    personalEmail: '',
    scholarship: '',
    scholarshipValue: '',
    sexualOrientation: '',
    sexualOrientationValue: '',
    ethnicGroup: '',
    ethnicGroupValue: '',
    victimPopulation: '',
    victimPopulationValue: '',
    otherVictimValue: '',
    displacementTime: '',
    displacementPlace: '',
    disability: '',
    disabilityValue: '',
    chronicDisability: '',
    chronicDisabilityValue: '',
    differentialFocus: [],
    attendantName: '',
    attendantEmail: '',
    attendantPhone: '',
    maritalStatus: '',
    maritalStatusValue: '',
    organization: '',
    organizationValue: '',
    occupation: '',
    occupationValue: '',
    userId: '',
    riskFactor: '',
    riskFactorValue: '',
    farmer: false,
    externalId: null,
    nationality: '',
    schoolId: null,
    groupId: null,
    studyCurrently: '',
    gradeOrStudy: '',
    obtainedTitle: '',
    lgbtiq: '',
    victimRegistry: '',
    nnaRightsProcess: '',
    disabilityCertificate: '',
    observations: '',
    isFarmer: ''
  });
  const [typesId, setTypesId] = useState([]);
  const [gender, setGender] = useState([]);
  const [zones, setZones] = useState([]);
  const [schoolLevel, setSchoolLevel] = useState([]);
  const [sexualOrientation, setSexualOrientation] = useState([]);
  const [disabilities, setDisabilities] = useState([]);
  const [studyGrades, setStudyGrades] = useState([]);
  const [riskOptions, setRiskOptions] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [isCheckedTerms, setIsCheckedTerms] = useState(false);
  const [edit, setEdit] = useState(false);
  const [nationalityOther, setNationalityOther] = useState('');
  const [sexualOrientationOther, setSexualOrientationOther] = useState('');
  const [disabilityOther, setDisabilityOther] = useState('');
  const [riskFactorOther, setRiskFactorOther] = useState('');
  const [showSaveConfirmationModal, setShowSaveConfirmationModal] =
    useState(false);
  const [pendingFields, setPendingFields] = useState([]);
  const signatureRef = useRef(null);
  const [batchMeetings, setBatchMeetings] = useState([]);
  const [selectedBatchMeetingIds, setSelectedBatchMeetingIds] = useState([]);
  const [hasBatchSignature, setHasBatchSignature] = useState(false);
  const [savingBatchAttendance, setSavingBatchAttendance] = useState(false);
  const [publicAttendanceContext, setPublicAttendanceContext] = useState(null);
  const [publicAttendanceError, setPublicAttendanceError] = useState('');
  const [publicRegistration, setPublicRegistration] = useState(null);

  const [age, setAge] = useState(0);

  const {
    paramValue,
    schoolParam,
    groupParam,
    activityGroupParam,
    bid
  } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const backTo = location.state?.backTo;
  const publicAttendanceToken = useMemo(() => {
    return new URLSearchParams(location.search).get('token') || '';
  }, [location.search]);
  const isPublicAttendanceMode =
    location.pathname.startsWith('/registro-jornada') &&
    Boolean(publicAttendanceToken);
  const isPublicCharacterizationOnly =
    isPublicAttendanceMode &&
    publicAttendanceContext?.registrationMode === 'characterization_only';
  const effectiveGroupParam = isPublicAttendanceMode
    ? publicAttendanceContext?.group?.id
    : groupParam;
  const effectiveSchoolParam = isPublicAttendanceMode
    ? publicAttendanceContext?.school?.id
    : schoolParam;
  const effectiveActivityGroupParam = isPublicAttendanceMode
    ? publicAttendanceContext?.activityGroup?.id
    : activityGroupParam ??
      location.state?.activityGroup?.id ??
      null;
  const isAttendanceBatchMode =
    (isPublicAttendanceMode && !isPublicCharacterizationOnly) ||
    location.pathname.startsWith('/caracterizacion-jornada') ||
    location.state?.attendanceBatch === true;
  const isControlSocialCharacterization = Boolean(
    effectiveActivityGroupParam ||
    location.state?.controlSocialContext ||
    publicAttendanceContext?.contextType === 'social_control'
  );
  const isCopCharacterization =
    (
      isPublicAttendanceMode
        ? isControlSocialCharacterization || publicAttendanceContext?.contextType === 'cop'
        :
      usesCompactAttendanceCharacterization(
        userRole || localStorage.getItem('ROLE')
      ) ||
      location.state?.isCopCharacterization === true
    );
  const resolvedOrganization = isControlSocialCharacterization
    ? getControlSocialOrganizationValue(
        personalInfo.organization,
        controlSocialOrganizationOther
      )
    : String(personalInfo.organization || '').trim();
  const isCopOrganizationTooLong =
    isCopCharacterization &&
    resolvedOrganization.length > COP_ORGANIZATION_MAX_LENGTH;

  const diseases = [
    {
      id: 'Si',
      code: 'Si',
      value: 'Si'
    },
    {
      id: 'No',
      code: 'No',
      value: 'No'
    }
  ];

  const nationalityList = [
    {
      id: 'CO',
      code: 'COLOMBIANO',
      value: 'Colombiano(a)'
    },
    {
      id: 'VE',
      code: 'VENEZOLANO',
      value: 'Venezolano(a)',
    },
    {
      id: 'OT',
      code: 'OTRO',
      value: 'Otro',
    }
  ];

  // METHODS //
  function isOtherValue(value) {
    return isOtherOptionValue(value);
  }

  function getOptionValue(options, selectedId) {
    if (!options?.length || selectedId === undefined || selectedId === null) {
      return undefined;
    }
    const match = options.find(
      opt => opt?.id?.toString() === selectedId?.toString()
    );
    return match?.value;
  }

  function isOtherSelected(options, selectedId, selectedValue) {
    const selectedIds = String(selectedId || '').split('|');
    const selectedValues = String(selectedValue || '').split('|');
    return selectedIds.some(id => isOtherValue(getOptionValue(options, id))) ||
      selectedValues.some(isOtherValue);
  }

  function getSelectedOtherValue(options, selectedId, selectedValue) {
    const selectedIds = String(selectedId || '').split('|');
    const selectedValues = String(selectedValue || '').split('|');
    const otherIndex = selectedIds.findIndex(id =>
      isOtherValue(getOptionValue(options, id))
    );

    if (otherIndex >= 0) {
      return String(selectedValues[otherIndex] || '')
        .replace(/^otro\s*:/i, '')
        .trim();
    }

    const selectedOtherValue = selectedValues.find(isOtherValue);
    if (selectedOtherValue) {
      return selectedOtherValue.replace(/^otro\s*:/i, '').trim();
    }

    return '';
  }

  function getExceededOtherField() {
    const otherFieldRules = [
      {
        active: personalInfo.nationality === 'OT',
        label: 'Nacionalidad',
        maxLength: 90,
        value: nationalityOther
      },
      {
        active: isOtherSelected(
          sexualOrientation,
          personalInfo.sexualOrientation,
          personalInfo.sexualOrientationValue
        ),
        label: 'Orientacion sexual',
        maxLength: 12,
        value: sexualOrientationOther
      },
      {
        active:
          personalInfo.victimPopulationValue === KeyConstant.ANOTHER_OPTION,
        label: 'Poblacion victima',
        maxLength: 255,
        value: personalInfo.otherVictimValue
      },
      {
        active: isOtherSelected(
          disabilities,
          personalInfo.disability,
          personalInfo.disabilityValue
        ),
        label: 'Enfoque de discapacidad',
        maxLength: 255,
        value: disabilityOther
      },
      {
        active: isOtherSelected(
          riskOptions,
          personalInfo.riskFactor,
          personalInfo.riskFactorValue
        ),
        label: 'Problematica o factor de riesgo',
        maxLength: 255,
        value: riskFactorOther
      },
      {
        active: isCopCharacterization,
        label: 'Organizacion',
        maxLength: COP_ORGANIZATION_MAX_LENGTH,
        value: resolvedOrganization
      }
    ];

    return (
      otherFieldRules.find(
        ({ active, maxLength, value }) =>
          active && String(value ?? '').length > maxLength
      ) || null
    );
  }

  function isEmptyFieldValue(value) {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) || value === 0;
    }

    if (typeof value === 'string') {
      const normalizedValue = value.trim();
      return !normalizedValue || normalizedValue.includes('Seleccione');
    }

    if (Array.isArray(value)) {
      return value.length === 0;
    }

    return false;
  }

  function getMandatoryFields() {
    return [
      { id: 'gender', name: 'Sexo al nacer' },
      { id: 'birthdate', name: 'Fecha de nacimiento' },
      { id: 'nationality', name: 'Nacionalidad' },
      { id: 'zone', name: 'Zona' },
      { id: 'municipality', name: 'Comuna/Corregimiento' },
      { id: 'neighborhood', name: 'Barrio/Vereda' },
      { id: 'cellPhone', name: 'Telefono de contacto' },
      { id: 'studyCurrently', name: 'Estudia actualmente' },
      { id: 'gradeOrStudy', name: 'Grado o estudio que cursa' },
      { id: 'scholarship', name: 'Nivel de escolaridad' },
      { id: 'lgbtiq', name: 'Comunidad LGBTIQ+' },
      { id: 'sexualOrientation', name: 'Orientacion sexual' },
      { id: 'ethnicGroup', name: 'Etnia' },
      { id: 'victimPopulation', name: 'Poblacion victima' },
      { id: 'isFarmer', name: 'Campesino' },
      { id: 'disability', name: 'Enfoque de discapacidad' },
      { id: 'riskFactor', name: 'Problematicas y factores de riesgo' }
    ];
  }

  function getPendingCharacterizationFields() {
    const fieldsToCheck = [
      { id: 'idType', name: 'Tipo de identificacion' },
      { id: 'address', name: 'Direccion' },
      { id: 'personalEmail', name: 'Correo electronico' },
      ...getMandatoryFields()
    ];

    const missingFields = fieldsToCheck
      .filter(({ id }) => isEmptyFieldValue(personalInfo[id]))
      .map(({ name }) => name);

    if (
      personalInfo.nationality === 'OT' &&
      isEmptyFieldValue(nationalityOther)
    ) {
      missingFields.push('Nacionalidad');
    }


    if (
      isOtherSelected(
        sexualOrientation,
        personalInfo.sexualOrientation,
        personalInfo.sexualOrientationValue
      ) &&
      isEmptyFieldValue(sexualOrientationOther)
    ) {
      missingFields.push('Orientacion sexual');
    }

    if (
      isOtherSelected(
        disabilities,
        personalInfo.disability,
        personalInfo.disabilityValue
      ) &&
      isEmptyFieldValue(disabilityOther)
    ) {
      missingFields.push('Enfoque de discapacidad');
    }

    if (
      isOtherSelected(
        riskOptions,
        personalInfo.riskFactor,
        personalInfo.riskFactorValue
      ) &&
      isEmptyFieldValue(riskFactorOther)
    ) {
      missingFields.push('Problematicas y factores de riesgo');
    }

    return Array.from(new Set(missingFields));
  }

  function parseLocalDate(value) {
    if (!value) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }
    const str = String(value).trim();
    if (!str) return null;
    const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      const localDate = new Date(0);
      localDate.setFullYear(year, month, day);
      localDate.setHours(0, 0, 0, 0);
      if (
        localDate.getFullYear() !== year ||
        localDate.getMonth() !== month ||
        localDate.getDate() !== day
      ) {
        return null;
      }
      return Number.isNaN(localDate.getTime()) ? null : localDate;
    }
    const parsed = new Date(str);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  function formatDateForInput(dateValue) {
    const date = parseLocalDate(dateValue);
    if (!date) {
      return '';
    }
    const year = String(date.getFullYear()).padStart(4, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function isCompleteDateInputValue(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''));
  }

  function isValidBirthdateValue(value) {
    const normalizedBirthdate = formatDateForInput(value);
    if (!isCompleteDateInputValue(normalizedBirthdate)) {
      return false;
    }

    const birthdate = parseLocalDate(normalizedBirthdate);
    if (!birthdate || birthdate.getFullYear() < 1900) {
      return false;
    }

    return birthdate.getTime() <= new Date().getTime();
  }

  function isMinimumAgeValid(value, minimumAge = 6) {
    const birthdate = parseLocalDate(formatDateForInput(value));
    if (!birthdate) return false;

    const today = new Date();
    let currentAge = today.getFullYear() - birthdate.getFullYear();
    const monthDifference = today.getMonth() - birthdate.getMonth();
    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthdate.getDate())
    ) {
      currentAge--;
    }
    return currentAge >= minimumAge;
  }

  function getMinimumBirthdateLimit(minimumAge = 6) {
    const limit = new Date();
    limit.setFullYear(limit.getFullYear() - minimumAge);
    return formatDateForInput(limit);
  }

  async function fileToBase64(file) {
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('FILE_READ_ERROR'));
      reader.readAsDataURL(file);
    });
  }

  function normalizeFileNameSegment(value, fallback) {
    const normalized = String(value || '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_.-]+/g, '_')
      .replace(/^_+|_+$/g, '');

    return normalized || fallback;
  }

  function ensurePdfFileName(value, fallbackBase = 'consentimiento') {
    const normalizedValue = String(value || '').trim();
    if (normalizedValue && /\.pdf$/i.test(normalizedValue)) {
      return normalizedValue;
    }

    return `${normalizeFileNameSegment(
      normalizedValue || fallbackBase,
      fallbackBase
    )}.pdf`;
  }

  function downloadBlob(blob, fileName) {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  }

  function resolveDownloadFileName(contentDisposition, fallback) {
    const headerValue = String(contentDisposition || '');
    if (!headerValue) return fallback;

    const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const basicMatch = headerValue.match(/filename="?([^"]+)"?/i);
    if (basicMatch?.[1]) {
      return basicMatch[1];
    }

    return fallback;
  }

  function onConsentFileChange(event) {
    const file = event?.target?.files?.[0];
    if (!file) {
      setConsentFile(null);
      setConsentFileName('');
      return;
    }
    const isPdf =
      file.type === 'application/pdf' ||
      file.name?.toLowerCase()?.endsWith('.pdf');

    if (!isPdf) {
      toast('Solo se permite archivo PDF para el consentimiento', {
        type: 'warning'
      });
      event.target.value = '';
      setConsentFile(null);
      setConsentFileName('');
      return;
    }

    setConsentFile(file);
    setConsentFileName(file.name);
    event.target.value = '';
  }

  function handleOpenSelectedConsent() {
    if (!consentPreviewUrl) return;
    window.open(consentPreviewUrl, '_blank', 'noopener,noreferrer');
  }

  function handleDownloadSelectedConsent() {
    if (!consentFile) return;
    downloadBlob(
      consentFile,
      ensurePdfFileName(consentFileName || consentFile.name)
    );
  }

  function handleClearSelectedConsent() {
    setConsentFile(null);
    setConsentFileName('');
  }

  function handleOpenStoredConsent() {
    if (!consentImageId) return;

    window.open(
      buildProtectedImageUrl(consentImageId, false, Date.now()),
      '_blank',
      'noopener,noreferrer'
    );
  }

  async function handleDownloadStoredConsent() {
    if (!consentImageId || isDownloadingStoredConsent) return;

    setIsDownloadingStoredConsent(true);

    try {
      const response = await fetchWithAuth(
        buildProtectedImageUrl(consentImageId, false, Date.now()),
        {
          method: 'GET'
        }
      );

      if (!response.ok) {
        throw new Error('CONSENT_DOWNLOAD_FAILED');
      }

      const fileBlob = await response.blob();
      const fallbackFileName = ensurePdfFileName(
        storedConsentFileName ||
          `consentimiento_${personalInfo.idNumber || 'beneficiario'}`
      );
      const headerFileName = resolveDownloadFileName(
        response.headers.get('content-disposition'),
        ''
      );
      const fileName =
        headerFileName &&
        headerFileName.toLowerCase().startsWith('consent')
          ? ensurePdfFileName(headerFileName)
          : fallbackFileName;

      downloadBlob(fileBlob, fileName);
      toast('Descarga de consentimiento iniciada', {
        type: 'success'
      });
    } catch (error) {
      console.error(error);
      toast('No se pudo descargar el consentimiento', {
        type: 'error'
      });
    } finally {
      setIsDownloadingStoredConsent(false);
    }
  }

  function navigateToOrigin() {
    if (isPublicAttendanceMode) {
      return false;
    }

    if (backTo?.pathname) {
      navigate(backTo.pathname, {
        state: backTo.state,
        replace: true
      });
      return true;
    }

    if (effectiveGroupParam && effectiveSchoolParam) {
      navigate(`/escuelas/grupo/${effectiveGroupParam}/school/${effectiveSchoolParam}`, {
        replace: true
      });
      return true;
    }

    if (effectiveActivityGroupParam) {
      navigate('/escuelas', {replace: true});
      return true;
    }

    if (effectiveSchoolParam) {
      navigate('/escuelas', {
        replace: true
      });
      return true;
    }

    return false;
  }

  function normalizeMeetingId(meeting) {
    return Number(meeting?.id ?? meeting?.meetingGroupId ?? meeting);
  }

  function getMeetingLabel(meeting) {
    const order = meeting?.order ?? meeting?.order_number ?? '';
    const type = meeting?.type === 'MEETING' ? 'Encuentro' : 'Evento';
    return `${type}${order ? ` ${order}` : ''}`;
  }

  function getMeetingDateLabel(meeting) {
    const rawDate = meeting?.meeting_date ?? meeting?.meetingDate;
    if (!rawDate) return 'Sin fecha registrada';
    const parsed = new Date(rawDate);
    if (Number.isNaN(parsed.getTime())) return 'Sin fecha registrada';
    return parsed.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  function validateBatchAttendanceFields() {
    if (!isAttendanceBatchMode) return true;

    if (selectedBatchMeetingIds.length === 0) {
      toast('Selecciona al menos una sesion para firmar', {
        type: 'warning'
      });
      return false;
    }

    if (!hasBatchSignature || !signatureRef.current?.hasDrawn()) {
      toast('Debe firmar antes de guardar la asistencia', {
        type: 'warning'
      });
      return false;
    }

    return true;
  }

  async function saveBatchAttendanceForBeneficiary(beneficiaryId) {
    if (!isAttendanceBatchMode) return true;

    if (!signatureRef.current?.hasDrawn()) {
      toast('Debe firmar antes de guardar la asistencia', {
        type: 'warning'
      });
      return false;
    }

    const signature = signatureRef.current?.toDataURL();
    if (!signature) {
      toast('Debe firmar antes de guardar la asistencia', {
        type: 'warning'
      });
      return false;
    }

    setSavingBatchAttendance(true);
    try {
      const response = await saveBatchMeetingAttendance({
        beneficiaryId,
        meetingIds: selectedBatchMeetingIds,
        signature,
        notes: 'Firma registrada desde caracterizacion de jornada'
      });

      return Boolean(response);
    } finally {
      setSavingBatchAttendance(false);
    }
  }

  async function savePublicAttendanceForBeneficiary(payload) {
    if (!isPublicCharacterizationOnly && !signatureRef.current?.hasDrawn()) {
      toast('Debe firmar antes de guardar la asistencia', {
        type: 'warning'
      });
      return false;
    }

    const signature = isPublicCharacterizationOnly
      ? undefined
      : signatureRef.current?.toDataURL();
    if (!isPublicCharacterizationOnly && !signature) {
      toast('Debe firmar antes de guardar la asistencia', {
        type: 'warning'
      });
      return false;
    }

    const publicPayload = { ...payload };
    delete publicPayload.externalId;
    delete publicPayload.groupId;
    delete publicPayload.schoolId;
    delete publicPayload.userId;

    setSavingBatchAttendance(true);
    try {
      const response = await registerPublicAttendance({
        token: publicAttendanceToken,
        beneficiary: publicPayload,
        meetingIds: selectedBatchMeetingIds,
        signature,
        notes: 'Firma registrada desde QR de caracterizacion'
      });

      return Boolean(response);
    } finally {
      setSavingBatchAttendance(false);
    }
  }

  async function save() {
    if (validateFields()) {
      try {
        if (!validateBatchAttendanceFields()) {
          return;
        }

        const isEditMode = Boolean(edit || personalInfo.externalId);
        const exceededOtherField = getExceededOtherField();
        if (exceededOtherField) {
          toast(`Muchos caracteres en ${exceededOtherField.label}`, {
            type: 'warning'
          });
          return;
        }

        if (String(personalInfo.disabilityValue || '').length > 90) {
          toast('La combinacion de enfoques de discapacidad es demasiado larga', {
            type: 'warning'
          });
          return;
        }

        if (String(personalInfo.riskFactorValue || '').length > 255) {
          toast('La combinacion de factores de riesgo es demasiado larga', {
            type: 'warning'
          });
          return;
        }

        const normalizedIdType = personalInfo.idType || 'CC';
        const normalizedEmail = personalInfo.personalEmail || '';
        const policyAccepted = Boolean(isCheckedTerms);
        const policyAcceptedAt = policyAccepted
          ? new Date().toISOString()
          : null;
        let payload = {
          ...personalInfo,
          victimRegistry: null,
          nnaRightsProcess: null,
          disabilityCertificate: null,
          chronicDisability: null,
          chronicDisabilityValue: null,
          maritalStatus: isCopCharacterization ? personalInfo.maritalStatus : '',
          maritalStatusValue: isCopCharacterization
            ? personalInfo.maritalStatusValue
            : '',
          obtainedTitle: isCopCharacterization ? personalInfo.obtainedTitle : '',
          occupation: isCopCharacterization ? personalInfo.occupation : '',
          occupationValue: isCopCharacterization ? personalInfo.occupationValue : '',
          otherVictimValue: isCopCharacterization
            ? personalInfo.otherVictimValue
            : '',
          displacementTime: isCopCharacterization
            ? personalInfo.displacementTime
            : null,
          displacementPlace: isCopCharacterization
            ? personalInfo.displacementPlace
            : null,
          userId,
          idType: normalizedIdType,
          personalEmail: normalizedEmail,
          dataPolicyAccepted: policyAccepted,
          dataPolicyAcceptedAt: policyAcceptedAt,
          nationality:
            personalInfo.nationality === 'OT'
              ? nationalityOther || personalInfo.nationality
              : personalInfo.nationality,
          groupId: effectiveGroupParam
            ? Number(effectiveGroupParam)
            : personalInfo.groupId
              ? Number(personalInfo.groupId)
              : null,
          schoolId: effectiveSchoolParam
            ? Number(effectiveSchoolParam)
            : personalInfo.schoolId
              ? Number(personalInfo.schoolId)
              : null,
          activityGroupId: effectiveActivityGroupParam
            ? Number(effectiveActivityGroupParam)
            : personalInfo.activityGroupId
              ? Number(personalInfo.activityGroupId)
              : null
        };

        if (isCopCharacterization) {
          payload = buildCopPayload(payload);
        } else {
          if (
            isOtherSelected(
              sexualOrientation,
              personalInfo.sexualOrientation,
              personalInfo.sexualOrientationValue
            ) &&
            sexualOrientationOther
          ) {
            payload.sexualOrientation = sexualOrientationOther;
            payload.sexualOrientationValue = 'Otro';
          }

          if (
            isOtherSelected(
              disabilities,
              personalInfo.disability,
              personalInfo.disabilityValue
            ) &&
            disabilityOther
          ) {
            payload.disabilityValue = String(payload.disabilityValue || '')
              .split('|')
              .map(value => isOtherValue(value) ? `Otro: ${disabilityOther}` : value.trim())
              .join(' | ');
          }

          if (
            isOtherSelected(
              riskOptions,
              personalInfo.riskFactor,
              personalInfo.riskFactorValue
            ) &&
            riskFactorOther
          ) {
            payload.riskFactorValue = String(payload.riskFactorValue || '')
              .split('|')
              .map(value => isOtherValue(value) ? `Otro: ${riskFactorOther}` : value.trim())
              .join(' | ');
          }
        }

        if (isPublicAttendanceMode) {
          const publicSaved = await savePublicAttendanceForBeneficiary(payload);
          if (!publicSaved) {
            return;
          }

          setPublicRegistration({
            fullName: payload.fullName,
            document: payload.idNumber
          });
          toast(isPublicCharacterizationOnly
            ? 'Caracterizacion guardada satisfactoriamente'
            : 'Caracterizacion y asistencia guardadas satisfactoriamente', {
            type: 'success'
          });
          return;
        }

        if (consentFile && !isCopCharacterization) {
          const base64 = await fileToBase64(consentFile);
          payload.consentFile = { base64 };
        }

        const res = await httpInterceptor.post(`beneficiary`, payload);
        if (res && res.success) {
          const savedBeneficiaryId = res.data;

          if (
            !isEditMode &&
            effectiveGroupParam &&
            !effectiveActivityGroupParam
          ) {
            try {
              const addResponse = await addToGroup(
                effectiveGroupParam,
                savedBeneficiaryId,
                effectiveSchoolParam
              );
              if (!addResponse || addResponse?.blocked) {
                return;
              }
            } catch (error) {
              console.error(error);
              toast('Beneficiario ya existe', {
                type: 'error'
              });
              return;
            }
          }

          const batchSaved = await saveBatchAttendanceForBeneficiary(
            savedBeneficiaryId
          );
          if (!batchSaved) {
            return;
          }

          toast(
            isAttendanceBatchMode
              ? 'Caracterizacion y asistencia guardadas satisfactoriamente'
              : isEditMode
                ? 'Beneficiario Actualizado Satisfactoriamente'
                : 'Beneficiario Creado Satisfactoriamente',
            {
              type: 'success'
            }
          );

          if (isEditMode) {
            if (!navigateToOrigin()) {
              navigate('/');
            }
          } else if (effectiveActivityGroupParam) {
            if (!navigateToOrigin()) {
              navigate('/escuelas', {replace: true});
            }
          } else if (effectiveGroupParam) {
            if (!navigateToOrigin()) {
              navigate(
                `/escuelas/grupo/${effectiveGroupParam}/school/${effectiveSchoolParam}`,
                { replace: true }
              );
            }
          } else if (effectiveSchoolParam) {
            if (!navigateToOrigin()) {
              navigate('/escuelas', { replace: true });
            }
          } else {
            navigate('/');
          }
        }
      } catch (error) {
        const exceededOtherField = getExceededOtherField();
        if (exceededOtherField) {
          toast(`Muchos caracteres en ${exceededOtherField.label}`, {
            type: 'warning'
          });
          return;
        }

        const backendMessage = error?.response?.data?.message;
        const canReactivate = Boolean(error?.response?.data?.data?.canReactivate);

        if (backendMessage === 'MINIMUM_AGE_REQUIRED') {
          toast('La persona debe tener al menos 6 años', {
            type: 'warning'
          });
        } else if (backendMessage === 'INVALID_BENEFICIARY_DATA') {
          toast('Debes diligenciar todos los campos obligatorios', {
            type: 'warning'
          });
        } else if (backendMessage === 'BENEFICIARY_CONTEXT_DUPLICATE') {
          toast(
            canReactivate
              ? 'Este documento ya existe inactivo en este lugar. Puedes reactivarlo desde agregar beneficiario.'
              : 'Este número de documento ya está caracterizado',
            {
              type: 'warning'
            }
          );
        } else if (backendMessage?.code === '23505') {
          toast('Beneficiario ya existe', {
            type: 'error'
          });
        } else if (error?.message === 'FILE_READ_ERROR') {
          toast('No se pudo procesar el PDF del consentimiento', {
            type: 'error'
          });
        } else {
          console.error(error);
          toast('Error creando beneficiario', {
            type: 'error'
          });
        }
      }
    }
  }

  async function addToGroup(groupId, beneficiaryId, schoolId) {
    return await addBeneficiary(groupId, beneficiaryId, schoolId);
  }

  function validateEmail(value) {
    if (!value) {
      return true;
    }
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(value);
  }

  function validatePhone(value) {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(String(value || ''));
  }

  function validateOnlyNumbers(value) {
    const numberRegex = /^[0-9]+$/;
    return numberRegex.test(String(value || ''));
  }

  function onlyNumbers(value, maxLength = null) {
    const normalizedValue = String(value || '').replace(/\D/g, '');
    if (!maxLength) {
      return normalizedValue;
    }
    return normalizedValue.slice(0, maxLength);
  }

  function validateCopFields() {
    const requiredFields = [
      { id: 'idType', name: 'Tipo de documento' },
      { id: 'idNumber', name: 'Numero de documento' },
      { id: 'fullName', name: 'Nombres y apellidos completos' },
      {
        id: 'organization',
        name: isControlSocialCharacterization
          ? 'Tipo de Organización'
          : 'Organizacion'
      },
      {
        id: 'companyEntity',
        name: isControlSocialCharacterization
          ? 'Nombre de la organización'
          : 'Empresa/Entidad/Dependencia'
      },
      { id: 'occupationValue', name: 'Cargo/Empleo/Calidad en la que actua' },
      { id: 'gender', name: 'Sexo' },
      { id: 'ageRange', name: 'Rango edad' },
      { id: 'differentialFocus', name: 'Enfoque diferencial' },
      { id: 'victimPopulation', name: 'Poblacion victima' },
      { id: 'municipality', name: 'Comuna en la que reside' },
      { id: 'personalEmail', name: 'Correo electronico' },
      { id: 'cellPhone', name: 'Número de contacto' }
    ];

    const missingField = requiredFields.find(({ id }) =>
      isEmptyFieldValue(personalInfo[id])
    );

    if (missingField) {
      toast(`El campo ${missingField.name} es obligatorio`, {
        type: 'warning'
      });
      return false;
    }

    if (
      isControlSocialCharacterization &&
      isOtherControlSocialOrganization(personalInfo.organization) &&
      !controlSocialOrganizationOther.trim()
    ) {
      toast('Debes especificar la organización', {
        type: 'warning'
      });
      return false;
    }

    if (!personalInfo.fullName || personalInfo.fullName.length < 3) {
      toast('Por favor ingresa un nombre valido', {
        type: 'warning'
      });
      return false;
    }

    if (!validateOnlyNumbers(personalInfo.idNumber)) {
      toast('El numero de documento solo debe contener digitos', {
        type: 'warning'
      });
      return false;
    }

    if (personalInfo.personalEmail && !validateEmail(personalInfo.personalEmail)) {
      toast('Debes ingresar un email valido', {
        type: 'warning'
      });
      return false;
    }

    if (personalInfo.cellPhone && !validatePhone(personalInfo.cellPhone)) {
      toast('El numero de contacto debe tener 10 digitos', {
        type: 'warning'
      });
      return false;
    }

    if (isCopOrganizationTooLong) {
      toast('Demasiados caracteres', {
        type: 'warning'
      });
      return false;
    }

    if (!isCheckedTerms) {
      toast('Debes aceptar las politicas de tratamiento de datos', {
        type: 'warning'
      });
      return false;
    }

    return true;
  }

  function getSelectedCopDifferentialIds() {
    return parseCopDifferentialFocus(personalInfo.differentialFocus);
  }

  function toggleCopDifferentialFocus(optionId) {
    setPersonalInfo(prev => {
      const selectedIds = parseCopDifferentialFocus(prev.differentialFocus);
      let nextIds;

      if (optionId === 'NINGUNO') {
        nextIds = selectedIds.includes(optionId) ? [] : ['NINGUNO'];
      } else {
        const selectedWithoutNone = selectedIds.filter(id => id !== 'NINGUNO');
        nextIds = selectedWithoutNone.includes(optionId)
          ? selectedWithoutNone.filter(id => id !== optionId)
          : [...selectedWithoutNone, optionId];
      }

      return {
        ...prev,
        differentialFocus: nextIds
      };
    });
  }

  function onChangeCopMunicipality(param, value, relatedValue) {
    const selectedMunicipality = municipalities.find(
      item => item.id?.toString() === value?.toString()
    );

    setPersonalInfo(prev => ({
      ...prev,
      [param]: value,
      municipalityName: relatedValue,
      neighborhood: '',
      neighborhoodName: '',
      zone: selectedMunicipality?.zoneId ?? prev.zone
    }));
  }

  function buildCopPayload(payload) {
    const selectedDifferentialIds = parseCopDifferentialFocus(
      payload.differentialFocus
    );
    const selectedEthnicOptions = COP_DIFFERENTIAL_OPTIONS.filter(
      option => option.ethnicCode && selectedDifferentialIds.includes(option.id)
    );
    const hasLgbtiq = selectedDifferentialIds.includes('LGBTIQ');
    const hasStreetSituation = selectedDifferentialIds.includes('SITUACION_CALLE');
    const hasFarmer = selectedDifferentialIds.includes('CAMPESINO');
    const hasDisability = selectedDifferentialIds.includes('DISCAPACIDAD');
    const selectedAgeRange =
      COP_AGE_RANGE_OPTIONS.find(option => option.id === payload.ageRange)
        ?.value || payload.ageRange;
    const selectedOrganization = isControlSocialCharacterization
      ? getControlSocialOrganizationValue(
          payload.organization,
          controlSocialOrganizationOther
        )
      : String(payload.organization || '').trim();
    const beneficiaryPayload = { ...payload };
    delete beneficiaryPayload.ageRange;
    delete beneficiaryPayload.companyEntity;
    delete beneficiaryPayload.organization;
    delete beneficiaryPayload.organizationValue;

    return {
      ...beneficiaryPayload,
      differentialFocus: selectedDifferentialIds,
      birthdate: '',
      address: payload.companyEntity,
      neighborhood: '',
      nationality: '',
      maritalStatus: '',
      studyCurrently: '',
      gradeOrStudy: selectedAgeRange,
      scholarship: '',
      obtainedTitle: '',
      occupation: 'COP',
      occupationValue: payload.occupationValue,
      ethnicGroup: selectedEthnicOptions[0]?.ethnicCode || 'ET_99',
      ethnicGroupValue:
        selectedEthnicOptions.map(option => option.value).join(', ') || 'Ninguna',
      lgbtiq: hasLgbtiq ? 'Si' : 'No',
      riskFactor: hasStreetSituation ? 'RIESGO_02' : 'RIESGO_00',
      riskFactorValue: hasStreetSituation ? 'Situacion de calle' : 'Ninguna',
      farmer: hasFarmer,
      isFarmer: hasFarmer ? 'Si' : 'No',
      disability: hasDisability ? 'DIS_98' : 'DIS_99',
      disabilityValue: hasDisability ? 'Discapacidad' : 'No tiene discapacidad',
      disabilityCertificate: '',
      chronicDisability: '',
      chronicDisabilityValue: '',
      sexualOrientation: '',
      sexualOrientationValue: '',
      victimPopulation: payload.victimPopulation,
      victimPopulationValue: payload.victimPopulation,
      victimRegistry: payload.victimPopulation,
      otherVictimValue: '',
      displacementTime: '',
      displacementPlace: '',
      nnaRightsProcess: '',
      observations: selectedOrganization,
      attendantName: '',
      attendantEmail: '',
      attendantPhone: ''
    };
  }

  function validateFields() {
    let isValid = false;
    if (isCopCharacterization) {
      return validateCopFields();
    }

    if (!personalInfo.idType) {
      toast('Por favor seleccione un tipo de identificacion', {
        type: 'warning'
      });
      return isValid;
    }
    if (!personalInfo.idNumber) {
      toast('Por favor digita el numero de identificacion', {
        type: 'warning'
      });
      return isValid;
    }
    if (!personalInfo.fullName || personalInfo.fullName.length < 3) {
      toast('Por favor ingresa un nombre valido', {
        type: 'warning'
      });
      return isValid;
    }
    if (!validateOnlyNumbers(personalInfo.idNumber)) {
      toast('El numero de documento solo debe contener digitos', {
        type: 'warning'
      });
      return isValid;
    }
    if (!personalInfo.address || personalInfo.address.length < 4) {
      toast('Por favor ingresa una direccion valida', {
        type: 'warning'
      });
      return isValid;
    }
    if (!personalInfo.personalEmail) {
      toast('El campo correo electronico es obligatorio', {
        type: 'warning'
      });
      return isValid;
    }
    if (!validateEmail(personalInfo.personalEmail)) {
      toast('Debes ingresar un email valido', {
        type: 'warning'
      });
      return isValid;
    }

    if (!validatePhone(personalInfo.cellPhone)) {
      toast('Debes ingresar un celular valido', {
        type: 'warning'
      });
      return isValid;
    }

    if (!isValidBirthdateValue(personalInfo.birthdate)) {
      toast('Debe ingresar una fecha valida', {
        type: 'warning'
      });
      return isValid;
    }
    if (!isMinimumAgeValid(personalInfo.birthdate)) {
      toast('La persona debe tener al menos 6 años', {
        type: 'warning'
      });
      return isValid;
    }

    if (showAttendantFields) {
      const attendantFields = [
        { id: 'attendantName', name: 'Nombre del acudiente' },
        { id: 'attendantEmail', name: 'Correo electronico del acudiente' },
        { id: 'attendantPhone', name: 'Telefono del acudiente' }
      ];
      const missingAttendantField = attendantFields.find(({ id }) =>
        isEmptyFieldValue(personalInfo[id])
      );

      if (missingAttendantField) {
        toast(`El campo ${missingAttendantField.name} es obligatorio`, {
          type: 'warning'
        });
        return isValid;
      }

      if (!validateEmail(personalInfo.attendantEmail)) {
        toast('Debes ingresar un correo electronico valido para el acudiente', {
          type: 'warning'
        });
        return isValid;
      }

      if (!validatePhone(personalInfo.attendantPhone)) {
        toast('El telefono del acudiente debe tener 10 digitos', {
          type: 'warning'
        });
        return isValid;
      }
    }

    const mandatoryFields = getMandatoryFields();


    if (
      isOtherSelected(
        sexualOrientation,
        personalInfo.sexualOrientation,
        personalInfo.sexualOrientationValue
      ) &&
      !sexualOrientationOther
    ) {
      toast('Debes ingresar la orientacion sexual', {
        type: 'warning'
      });
      return isValid;
    }

    if (
      isOtherSelected(
        disabilities,
        personalInfo.disability,
        personalInfo.disabilityValue
      ) &&
      !disabilityOther
    ) {
      toast('Debes ingresar el enfoque de discapacidad', {
        type: 'warning'
      });
      return isValid;
    }

    if (
      isOtherSelected(
        riskOptions,
        personalInfo.riskFactor,
        personalInfo.riskFactorValue
      ) &&
      !riskFactorOther
    ) {
      toast('Debes ingresar la problematica o factor de riesgo', {
        type: 'warning'
      });
      return isValid;
    }

    if (personalInfo.nationality === 'OT' && !nationalityOther) {
      toast('Debes ingresar la nacionalidad', {
        type: 'warning'
      });
      return isValid;
    }

    const exceededOtherField = getExceededOtherField();
    if (exceededOtherField) {
      toast(`Muchos caracteres en ${exceededOtherField.label}`, {
        type: 'warning'
      });
      return isValid;
    }

    if (!isCheckedTerms) {
      toast('Debes aceptar las politicas de tratamiento de datos', {
        type: 'warning'
      });
      return isValid;
    }

    isValid = true;
    mandatoryFields.forEach(m => {
      const idName = personalInfo[`${m.id}`];
      if (!idName || idName?.toString()?.indexOf('Seleccione') !== -1) {
        toast(`El campo ${m.name} es obligatorio`, {
          type: 'warning'
        });
        isValid = false;
      }
    });
    return isValid;
  }

  function handleSaveClick() {
    if (
      edit ||
      isCopCharacterization ||
      isAttendanceBatchMode ||
      isPublicAttendanceMode
    ) {
      save();
      return;
    }

    if (!validateFields()) {
      return;
    }

    const missingFields = getPendingCharacterizationFields();

    if (missingFields.length === 0) {
      save();
      return;
    }

    setPendingFields(missingFields);
    setShowSaveConfirmationModal(true);
  }

  function confirmSave() {
    setShowSaveConfirmationModal(false);
    save();
  }

  function handleCancelClick() {
    if (navigateToOrigin()) {
      return;
    }

    navigate('/');
  }

  function applyListsData(data = []) {
    const typeId = data.filter(item => item.type === 'identification_type');
    if (typeId && typeId.length > 0) {
      setTypesId(ensureIdentificationTypeOptions(
        typeId.map(t => {
          return { id: t.code, value: t.value };
        })
      ));
    }

    const gender = data.filter(item => item.type === 'gender');
    setGender(gender);

    const schoolLevel = prioritizeNoneOption(
      data.filter(item => item.type === 'schooling_level')
    );
    setSchoolLevel(schoolLevel);

    const s = data
      .filter(item => item.type === 'sexual_orientation')
      .sort((a, b) => {
        const aIsHeterosexual =
          String(a?.value ?? '').trim().toLowerCase() === 'heterosexual' ||
          String(a?.code ?? '').trim().toLowerCase() === 'heterosexual';
        const bIsHeterosexual =
          String(b?.value ?? '').trim().toLowerCase() === 'heterosexual' ||
          String(b?.code ?? '').trim().toLowerCase() === 'heterosexual';

        if (aIsHeterosexual === bIsHeterosexual) return 0;
        return aIsHeterosexual ? -1 : 1;
      });
    setSexualOrientation(s);

    const disability = data.filter(item => item.type === 'disability');
    setDisabilities(disability);

    const studyGrades = prioritizeNoneOption(
      data.filter(item => item.type === 'study_grade')
    );
    setStudyGrades(studyGrades);

    const r = data.filter(item => item.type === 'risk_factor');
    setRiskOptions(r);

  }

  function getLists() {
    httpInterceptor.get(`/lists`).then(res => {
      let data = [];
      if (res?.status === 200) {
        data = res.data.data;
      } else if (res && res.success) {
        data = res.data;
      }
      applyListsData(data);
    });
    return () => {};
  }

  function addPersonalInfo(param, value, relatedValue) {
    const valueName = `${param}Value`;
    setPersonalInfo(prev => {
      if (prev[valueName] !== undefined) {
        return {
          ...prev,
          [param]: value,
          [valueName]: relatedValue
        };
      }
      return { ...prev, [param]: value };
    });
  }

  async function getBeneficiaryInfo(documentNumber) {
    if (documentNumber) {
      setPersonalInfo(prev => ({
        ...prev,
        idNumber: documentNumber,
        idType: 'CC'
      }));
    }
  }

  async function getBeneficiaryByUID(id) {
    if (id) {
      const beneficiary = await getBeneficiaryById(id);
      updateBeneficiaryInfo(beneficiary);
    }
  }

  function updateBeneficiaryInfo(beneficiary) {
    let info = {};
    if (beneficiary) {
      setEdit(true);
      info = {
        address: beneficiary.address,
        companyEntity: beneficiary.address ?? '',
        ageRange: beneficiary.grade_or_study ?? getCopAgeRangeFromAge(beneficiary.age),
        attendantEmail: beneficiary.parent_email,
        attendantName: beneficiary.parent,
        attendantPhone: beneficiary.parent_phone,
        birthdate: beneficiary.birth_date,
        cellPhone: beneficiary.cellphone,
        chronicDisability: beneficiary.chronic_disability_value,
        chronicDisabilityValue: beneficiary.chronic_disability_value,
        disability: beneficiary.disability,
        disabilityValue: beneficiary.disability_value,
        disabilityCertificate: beneficiary.disability_certificate ?? '',
        differentialFocus: buildCopDifferentialIdsFromBeneficiary(beneficiary),
        displacementPlace: beneficiary.displacement_place,
        displacementTime: beneficiary.displacement_time,
        ethnicGroup: beneficiary.ethnic_group,
        ethnicGroupValue: beneficiary.ethnic_group_value,
        externalId: beneficiary.id,
        farmer: beneficiary.farmer,
        isFarmer: beneficiary.farmer ? 'Si' : 'No',
        fullName: beneficiary.full_name,
        gender: beneficiary.gender,
        idNumber: beneficiary.document,
        idType: beneficiary.document_type,
        lgbtiq: beneficiary.lgbtiq ?? '',
        maritalStatus: beneficiary.marital_status,
        maritalStatusValue: beneficiary.marital_status_value,
        municipality: beneficiary.municipality_id,
        municipalityName: beneficiary.municipality?.name,
        neighborhood: beneficiary.neighborhood_id,
        neighborhoodName: beneficiary.neighborhood?.name,
        nnaRightsProcess: beneficiary.nna_rights_process ?? '',
        occupation: beneficiary.occupation,
        occupationValue: beneficiary.occupation_value,
        organization: beneficiary.observations ?? '',
        organizationValue: '',
        obtainedTitle: beneficiary.obtained_title ?? '',
        observations: beneficiary.observations ?? '',
        otherVictimValue: beneficiary.other_victim_value,
        personalEmail: beneficiary.email,
        riskFactor: beneficiary.risk_factor,
        riskFactorValue: beneficiary.risk_factor_value,
        studyCurrently: beneficiary.study_currently ?? '',
        scholarship: beneficiary.scholarship,
        scholarshipValue: beneficiary.scholarship_value,
        sexualOrientation: beneficiary.sexual_orientation,
        sexualOrientationValue: beneficiary.sexual_orientation_value,
        victimPopulation: beneficiary.victim_population,
        victimPopulationValue: beneficiary.victim_population_value,
        victimRegistry: beneficiary.victim_registry ?? '',
        zone: beneficiary.zone_id,
        nationality: beneficiary.nationality,
        gradeOrStudy: beneficiary.grade_or_study ?? '',
        schoolId: beneficiary.school_id ?? beneficiary.school?.id ?? null,
        groupId: beneficiary.group_id ?? beneficiary.group?.id ?? null,
      };
      if (isCopCharacterization) {
        if (isControlSocialCharacterization) {
          const storedOrganization = resolveStoredControlSocialOrganization(
            beneficiary.observations
          );
          info.organization = storedOrganization.organization;
          setControlSocialOrganizationOther(
            storedOrganization.otherOrganization
          );
        } else {
          setControlSocialOrganizationOther('');
        }
        const copVictimValue = resolveYesNo(
          beneficiary.victim_population_value ?? beneficiary.victim_population
        );
        if (copVictimValue !== null) {
          info.victimPopulation = copVictimValue ? 'Si' : 'No';
          info.victimPopulationValue = copVictimValue ? 'Si' : 'No';
        }
      }
      const normalizedNationality = String(beneficiary.nationality || '')
        .trim()
        .toUpperCase();
      if (
        normalizedNationality &&
        normalizedNationality !== 'COLOMBIANO' &&
        normalizedNationality !== 'VENEZOLANO' &&
        normalizedNationality !== 'OTRO'
      ) {
        info.nationality = 'OT';
        setNationalityOther(beneficiary.nationality);
      } else if (normalizedNationality === 'OTRO') {
        info.nationality = 'OT';
        setNationalityOther('');
      } else {
        setNationalityOther('');
      }

      const normalizedSexualValue = String(
        beneficiary.sexual_orientation_value || ''
      )
        .trim()
        .toLowerCase();
      if (normalizedSexualValue === 'otro') {
        info.sexualOrientation = 'Otro';
        setSexualOrientationOther(beneficiary.sexual_orientation ?? '');
      } else {
        setSexualOrientationOther('');
      }

      setDisabilityOther(
        getSelectedOtherValue(
          disabilities,
          beneficiary.disability,
          beneficiary.disability_value
        )
      );

      setRiskFactorOther(
        getSelectedOtherValue(
          riskOptions,
          beneficiary.risk_factor,
          beneficiary.risk_factor_value
        )
      );
      const birth = formatDateForInput(info.birthdate);
      setPersonalAge(birth);
      calculateAge(birth);
      setPersonalInfo(info);
      setIsCheckedTerms(Boolean(beneficiary.data_policy_accepted));
      setConsentFile(null);
      setConsentFileName('');
      if (beneficiary?.consentImage?.id) {
        setConsentImageId(beneficiary.consentImage.id);
        setStoredConsentFileName(
          beneficiary.consentImage.name || 'Consentimiento cargado'
        );
      } else {
        setConsentImageId(null);
        setStoredConsentFileName('');
      }
    }
  }

  async function getBasicInfo() {
    getLists();
    const [zonesData, municipalitiesData] = await Promise.all([
      getZones(),
      getMunicipalities()
    ]);
    if (zonesData) {
      setZones(
        zonesData.map(z => {
          return { id: z.id, value: z.name };
        })
      );
    }
    if (municipalitiesData) {
      setMunicipalities(
        municipalitiesData.map(m => {
          return { id: m.id, value: m.name, zoneId: m.zone_id };
        })
      );
    }

    return () => {};
  }

  function handleBirthdateChange(value) {
    const birth = String(value || '');
    setPersonalAge(birth);

    if (!birth) {
      setPersonalInfo(prev => ({ ...prev, birthdate: '' }));
      setAge(0);
      return;
    }

    if (!isCompleteDateInputValue(birth)) {
      return;
    }

    calculateAge(birth, { syncInput: false });
  }

  function calculateAge(birth, options = {}) {
    const { syncInput = true } = options;
    let fullAge = 0;
    if (birth) {
      const now = new Date();
      const normalizedBirth = formatDateForInput(birth);
      if (!isCompleteDateInputValue(normalizedBirth)) {
        return false;
      }
      const birthday = parseLocalDate(normalizedBirth);
      if (!birthday || birthday.getTime() > now.getTime()) {
        alert('Debe ingresar una fecha valida');
        return false;
      }
      fullAge = now.getFullYear() - birthday.getFullYear();
      const month = now.getMonth() - birthday.getMonth();
      if (month < 0 || (month === 0 && now.getDate() < birthday.getDate())) {
        fullAge--;
      }

      if (typeof fullAge === 'boolean') {
        if (!fullAge) {
          setPersonalInfo(prev => ({ ...prev, birthdate: '' }));
          setAge(0);
          return;
        }
      }
      setPersonalInfo(prev => ({ ...prev, birthdate: normalizedBirth }));
      setAge(fullAge);
      if (syncInput) {
        setPersonalAge(normalizedBirth);
      }
    }
    return fullAge;
  }

  async function onChangeZone(param, value, relatedValue) {
    await updateMunicipalities(value);
    addPersonalInfo(param, value, relatedValue);
  }

  async function updateMunicipalities(selectedId) {
    if (isPublicAttendanceMode) {
      const publicMunicipalities = Array.isArray(publicAttendanceContext?.municipalities)
        ? publicAttendanceContext.municipalities
        : [];
      const filteredMunicipalities = selectedId
        ? publicMunicipalities.filter(
          municipality => String(municipality.zone_id) === String(selectedId)
        )
        : publicMunicipalities;

      setMunicipalities(
        filteredMunicipalities.map(m => {
          return { id: m.id, value: m.name, zoneId: m.zone_id };
        })
      );
      return;
    }

    const data = await getMunicipalities(selectedId);
    if (data) {
      setMunicipalities(
        data.map(m => {
          return { id: m.id, value: m.name, zoneId: m.zone_id };
        })
      );
    }
  }

  async function onChangeMunicipality(param, value, relatedValue) {
    await updateNeighborhoods(value);
    addPersonalInfo(param, value, relatedValue);
  }

  async function updateNeighborhoods(selectedId) {
    const data = isPublicAttendanceMode
      ? await getPublicAttendanceNeighborhoods(publicAttendanceToken, selectedId)
      : await getNeighborhoods(selectedId);
    if (data) {
      setNeighborhoods(
        data.map(m => {
          return { id: m.id, value: m.name };
        })
      );
    }
  }

  useEffect(() => {
    if (!isPublicAttendanceMode) return undefined;

    let alive = true;
    setPublicAttendanceError('');

    (async () => {
      const context = await getPublicAttendanceContext(publicAttendanceToken);
      if (!alive) return;

      if (context?.error === ATTENDANCE_WINDOW_CLOSED) {
        setPublicAttendanceError(ATTENDANCE_CLOSED_MESSAGE);
        return;
      }

      if (!context) {
        setPublicAttendanceError('El enlace de registro no es valido o ya expiro.');
        return;
      }

      setPublicAttendanceContext(context);
      applyListsData(Array.isArray(context.lists) ? context.lists : []);
      setZones(
        (context.zones || []).map(z => ({ id: z.id, value: z.name }))
      );
      setMunicipalities(
        (context.municipalities || []).map(m => ({
          id: m.id,
          value: m.name,
          zoneId: m.zone_id
        }))
      );
      setBatchMeetings(Array.isArray(context.meetings) ? context.meetings : []);
      setPersonalInfo(prev => ({
        ...prev,
        schoolId: context.school?.id ?? null,
        groupId: context.group?.id ?? null
      }));
    })();

    return () => {
      alive = false;
    };
  }, [isPublicAttendanceMode, publicAttendanceToken]);

  useEffect(() => {
    if (isPublicAttendanceMode) return;
    getBasicInfo();
  }, [isPublicAttendanceMode]);

  useEffect(() => {
    if (isPublicAttendanceMode) return;
    getBeneficiaryInfo(paramValue);
  }, [paramValue, isPublicAttendanceMode]);

  useEffect(() => {
    if (isPublicAttendanceMode) return;
    getBeneficiaryByUID(bid);
  }, [bid, isPublicAttendanceMode]);

  useEffect(() => {
    if (!isAttendanceBatchMode) return undefined;
    if (isPublicAttendanceMode) {
      if (Array.isArray(publicAttendanceContext?.meetings)) {
        setBatchMeetings(publicAttendanceContext.meetings);
      }
      return undefined;
    }

    let alive = true;
    const stateMeetings = Array.isArray(location.state?.meetings)
      ? location.state.meetings
      : [];

    if (stateMeetings.length > 0) {
      setBatchMeetings(stateMeetings);
      return () => {
        alive = false;
      };
    }

    (async () => {
      if (!effectiveGroupParam && !effectiveActivityGroupParam) return;
      const meetings = effectiveActivityGroupParam
        ? await getMeetingsByActivityGroup(effectiveActivityGroupParam)
        : await getMeetingsByGroup(effectiveGroupParam);
      if (alive) {
        setBatchMeetings(Array.isArray(meetings) ? meetings : []);
      }
    })();

    return () => {
      alive = false;
    };
  }, [
    effectiveGroupParam,
    effectiveActivityGroupParam,
    isAttendanceBatchMode,
    isPublicAttendanceMode,
    location.state,
    publicAttendanceContext
  ]);

  useEffect(() => {
    if (!isAttendanceBatchMode || batchMeetings.length === 0) return;

    setSelectedBatchMeetingIds(
      batchMeetings
        .slice()
        .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))
        .map(normalizeMeetingId)
        .filter(Boolean)
    );
  }, [batchMeetings, isAttendanceBatchMode]);

  useEffect(() => {
    if (!consentFile) {
      setConsentPreviewUrl('');
      return;
    }

    const objectUrl = window.URL.createObjectURL(consentFile);
    setConsentPreviewUrl(objectUrl);

    return () => {
      window.URL.revokeObjectURL(objectUrl);
    };
  }, [consentFile]);

  const showAttendantFields =
    Boolean(personalInfo.birthdate) && Number(age) < 18;

  const hasStoredConsent = Boolean(consentImageId);
  const hasSelectedConsent = Boolean(consentFile && consentFileName);
  const selectedCopDifferentialIds = getSelectedCopDifferentialIds();
  const sortedBatchMeetings = useMemo(
    () =>
      batchMeetings
        .slice()
        .sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0)),
    [batchMeetings]
  );

  if (isPublicAttendanceMode && publicRegistration) {
    return (
      <main className='characterization characterization--public-state'>
        <section className='characterization-public-state'>
          <span className='material-symbols-rounded characterization-public-state__icon'>
            check_circle
          </span>
          <h2 className='characterization-public-state__title'>
            Registro completado
          </h2>
          <p className='characterization-public-state__text'>
            {isPublicCharacterizationOnly
              ? 'La caracterizacion fue guardada correctamente.'
              : 'La caracterizacion y la asistencia fueron guardadas correctamente.'}
          </p>
          <div className='characterization-public-state__summary'>
            <span>{publicRegistration.fullName}</span>
            <span>{publicRegistration.document}</span>
          </div>
          <button
            type='button'
            className='actions__save'
            onClick={() => window.location.reload()}>
            Registrar otra persona
          </button>
        </section>
      </main>
    );
  }

  if (isPublicAttendanceMode && publicAttendanceError) {
    return (
      <main className='characterization characterization--public-state'>
        <section className='characterization-public-state characterization-public-state--error'>
          <span className='material-symbols-rounded characterization-public-state__icon'>
            error
          </span>
          <h2 className='characterization-public-state__title'>
            Enlace no disponible
          </h2>
          <p className='characterization-public-state__text'>
            {publicAttendanceError}
          </p>
        </section>
      </main>
    );
  }

  if (isPublicAttendanceMode && !publicAttendanceContext) {
    return (
      <main className='characterization characterization--public-state'>
        <section className='characterization-public-state'>
          <span className='material-symbols-rounded characterization-public-state__icon'>
            progress_activity
          </span>
          <h2 className='characterization-public-state__title'>
            Cargando registro
          </h2>
        </section>
      </main>
    );
  }

  return (
    <>
      {showSaveConfirmationModal && (
        <ModalContainer>
          <section className='characterization-confirm-modal'>
            <h3 className='characterization-confirm-modal__title'>
              ¿Seguro quiere registrar el beneficiario ya?
            </h3>

            <p className='characterization-confirm-modal__text'>
              {pendingFields.length > 0
                ? `Faltan los campos: ${pendingFields.join(', ')} por llenar.`
                : 'No faltan campos por llenar.'}
            </p>

            <div className='characterization-confirm-modal__actions'>
              <button
                type='button'
                className='blue-site-button'
                onClick={confirmSave}>
                SI
              </button>

              <button
                type='button'
                className='button-white-border-blue'
                onClick={() => setShowSaveConfirmationModal(false)}>
                NO
              </button>
            </div>
          </section>
        </ModalContainer>
      )}

      <main className='characterization'>
      <div className='characterization__container c-content'>
        <div className='c-content__header head'>
          <h2 className='head__title'>
            {isAttendanceBatchMode ? 'Caracterizacion de jornada' : 'Caracterizacion'}
          </h2>
          <p className='head__subtitle'>
            {isAttendanceBatchMode
              ? 'Informacion del participante y firma de asistencia'
              : isPublicCharacterizationOnly
                ? 'Informacion del participante'
                : 'Informacion del beneficiario'}
          </p>
        </div>
        <div className='c-content__form form'>
          {isCopCharacterization ? (
            <>
              <div className='form__section section form__section--cop'>
                <div className='section__field field'>
                  <span className='field__text'>
                    Tipo de documento<p className='field__star field__star--cop-required'>*</p>
                  </span>
                  <Select
                    field='idType'
                    options={typesId}
                    selectedDef={personalInfo?.idType}
                    setSelected={(param, value, relatedValue) =>
                      addPersonalInfo(param, value, relatedValue)
                    }></Select>
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    Numero de documento<p className='field__star field__star--cop-required'>*</p>
                  </span>
                  <input
                    className='field__input'
                    name='idNumber'
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    value={personalInfo.idNumber}
                    onInput={({ target }) =>
                      addPersonalInfo(target.name, onlyNumbers(target.value))
                    }
                  />
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    Nombres y apellidos completos<p className='field__star field__star--cop-required'>*</p>
                  </span>
                  <input
                    className='field__input'
                    name='fullName'
                    type='text'
                    value={personalInfo.fullName}
                    onInput={({ target }) =>
                      addPersonalInfo(target.name, target.value)
                    }
                  />
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    {isControlSocialCharacterization
                      ? 'Tipo de Organización'
                      : 'Organización'}
                    <p className='field__star'>*</p>
                  </span>
                  {isControlSocialCharacterization ? (
                    <>
                      <Select
                        field='organization'
                        options={CONTROL_SOCIAL_ORGANIZATION_OPTIONS}
                        selectedDef={personalInfo.organization}
                        setSelected={(param, value, relatedValue) => {
                          addPersonalInfo(param, value, relatedValue);
                          if (!isOtherControlSocialOrganization(value)) {
                            setControlSocialOrganizationOther('');
                          }
                        }}
                      />
                      {isOtherControlSocialOrganization(
                        personalInfo.organization
                      ) && (
                        <>
                          <input
                            className={`field__input${isCopOrganizationTooLong ? ' field__input--error' : ''}`}
                            type='text'
                            name='otherOrganization'
                            placeholder='Especifique la organización'
                            value={controlSocialOrganizationOther}
                            aria-invalid={isCopOrganizationTooLong}
                            onInput={({ target }) =>
                              setControlSocialOrganizationOther(target.value)
                            }
                          />
                          {isCopOrganizationTooLong && (
                            <p className='field__error'>Demasiados caracteres</p>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        className={`field__input${isCopOrganizationTooLong ? ' field__input--error' : ''}`}
                        type='text'
                        name='organization'
                        value={personalInfo.organization}
                        aria-invalid={isCopOrganizationTooLong}
                        onInput={({ target }) =>
                          addPersonalInfo(target.name, target.value)
                        }
                      />
                      {isCopOrganizationTooLong && (
                        <p className='field__error'>Demasiados caracteres</p>
                      )}
                    </>
                  )}
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    {isControlSocialCharacterization
                      ? 'Nombre de la organización'
                      : 'Empresa/Entidad/Dependencia'}
                    <p className='field__star'>*</p>
                  </span>
                  <input
                    className='field__input'
                    name='companyEntity'
                    type='text'
                    value={personalInfo.companyEntity}
                    onInput={({ target }) =>
                      addPersonalInfo(target.name, target.value)
                    }
                  />
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    Cargo/Empleo/Calidad en la que actua<p className='field__star'>*</p>
                  </span>
                  <input
                    className='field__input'
                    name='occupationValue'
                    type='text'
                    value={personalInfo.occupationValue}
                    onInput={({ target }) =>
                      addPersonalInfo(target.name, target.value)
                    }
                  />
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    Sexo (Hombre/mujer)<p className='field__star'>*</p>
                  </span>
                  <Select
                    field='gender'
                    options={gender}
                    selectedDef={personalInfo.gender}
                    setSelected={(param, value, relatedValue) =>
                      addPersonalInfo(param, value, relatedValue)
                    }></Select>
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    Rango edad<p className='field__star'>*</p>
                  </span>
                  <Select
                    field='ageRange'
                    options={COP_AGE_RANGE_OPTIONS}
                    selectedDef={personalInfo.ageRange}
                    setSelected={(param, value, relatedValue) =>
                      addPersonalInfo(param, value, relatedValue)
                    }></Select>
                </div>
                <div className='section__field field field--all-row'>
                  <span className='field__text'>Enfoque diferencial</span>
                  <div className='cop-options'>
                    {COP_DIFFERENTIAL_OPTIONS.map(option => {
                      const inputId = `cop-focus-${option.id}`;
                      return (
                        <label className='cop-options__item' key={option.id} htmlFor={inputId}>
                          <input
                            id={inputId}
                            className='cop-options__input'
                            type='checkbox'
                            checked={selectedCopDifferentialIds.includes(option.id)}
                            onChange={() => toggleCopDifferentialFocus(option.id)}
                          />
                          <span className='cop-options__box' aria-hidden='true'></span>
                          <span className='cop-options__text'>{option.value}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className='form__section section form__section--cop'>
                <div className='section__field field'>
                  <span className='field__text'>
                    Poblacion victima<p className='field__star'>*</p>
                  </span>
                  <Select
                    field='victimPopulation'
                    options={diseases}
                    selectedDef={personalInfo.victimPopulation}
                    setSelected={(param, value, relatedValue) =>
                      addPersonalInfo(param, value, relatedValue)
                    }></Select>
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    Comuna en la que reside<p className='field__star'>*</p>
                  </span>
                  <Select
                    field='municipality'
                    options={municipalities}
                    selectedDef={personalInfo.municipality}
                    setSelected={(param, value, relatedValue) =>
                      onChangeCopMunicipality(param, value, relatedValue)
                    }></Select>
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    Correo electronico<p className='field__star'>*</p>
                  </span>
                  <input
                    className='field__input'
                    type='email'
                    name='personalEmail'
                    placeholder='Ejm: example@example.com'
                    value={personalInfo.personalEmail}
                    onInput={({ target }) =>
                      addPersonalInfo(target.name, target.value)
                    }
                  />
                </div>
                <div className='section__field field'>
                  <span className='field__text'>
                    Número de contacto<p className='field__star'>*</p>
                  </span>
                  <input
                    className='field__input'
                    name='cellPhone'
                    type='text'
                    inputMode='numeric'
                    pattern='[0-9]*'
                    maxLength={10}
                    value={personalInfo.cellPhone}
                    onInput={({ target }) =>
                      addPersonalInfo(target.name, onlyNumbers(target.value, 10))
                    }
                  />
                </div>
              </div>
            </>
          ) : (
            <>
          <div className='form__section section'>
            <div className='section__field field'>
              <span className='field__text'>
                Tipo de identificacion<p className='field__star'>*</p>
              </span>
              <Select
                field='idType'
                options={typesId}
                selectedDef={personalInfo?.idType}
                setSelected={(param, value, relatedValue) =>
                  addPersonalInfo(param, value, relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Numero de documento de identificacion<p className='field__star'>*</p>
              </span>
              <input
                className='field__input'
                name='idNumber'
                type='number'
                value={personalInfo.idNumber}
                onInput={({ target }) =>
                  addPersonalInfo(target.name, target.value)
                }
              />
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Nombres y apellidos completos<p className='field__star'>*</p>
              </span>
              <input
                className='field__input'
                name='fullName'
                type='text'
                value={personalInfo.fullName}
                onInput={({ target }) =>
                  addPersonalInfo(target.name, target.value)
                }
              />
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Telefono de contacto<p className='field__star'>*</p>
              </span>
              <input
                className='field__input'
                name='cellPhone'
                type='text'
                inputMode='numeric'
                pattern='[0-9]*'
                maxLength={10}
                value={personalInfo.cellPhone}
                onInput={({ target }) =>
                  addPersonalInfo(target.name, onlyNumbers(target.value, 10))
                }
              />
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Correo electronico<p className='field__star'>*</p>
              </span>
              <input
                className='field__input'
                type='email'
                name='personalEmail'
                placeholder='Ejm: example@example.com'
                value={personalInfo.personalEmail}
                onInput={({ target }) =>
                  addPersonalInfo(target.name, target.value)
                }
              />
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Direccion de residencia<p className='field__star'>*</p>
              </span>
              <input
                className='field__input'
                type='text'
                name='address'
                value={personalInfo.address}
                onInput={({ target }) =>
                  addPersonalInfo(target.name, target.value)
                }
              />
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Zona<p className='field__star'>*</p>
              </span>
              <Select
                field='zone'
                options={zones}
                selectedDef={personalInfo.zone}
                setSelected={(param, value, relatedValue) =>
                  onChangeZone(param, value, relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Comuna/Corregimiento<p className='field__star'>*</p>
              </span>
              <Select
                field='municipality'
                options={municipalities}
                selectedDef={personalInfo.municipality}
                disabled={!personalInfo.zone}
                setSelected={(param, value, relatedValue) =>
                  onChangeMunicipality(param, value, relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Barrio/Vereda<p className='field__star'>*</p>
              </span>
              <Select
                field='neighborhood'
                options={neighborhoods}
                selectedDef={personalInfo.neighborhood}
                disabled={!personalInfo.municipality}
                setSelected={(param, value, relatedValue) =>
                  addPersonalInfo(param, value, relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Nacionalidad<p className='field__star'>*</p>
              </span>
              <Select
                field='nationality'
                options={nationalityList}
                selectedDef={personalInfo?.nationality}
                setSelected={(param, value, relatedValue) => {
                  addPersonalInfo(param, value, relatedValue);
                  if (value !== 'OT') {
                    setNationalityOther('');
                  }
                }}></Select>
            </div>
            {personalInfo.nationality === 'OT' && (
              <div className='section__field field'>
                <span className='field__text'>Especifique nacionalidad</span>
                <input
                  className='field__input'
                  type='text'
                  value={nationalityOther}
                  onInput={({ target }) => setNationalityOther(target.value)}
                />
              </div>
            )}
            <div className='section__field field'>
              <span className='field__text'>
                Sexo al nacer<p className='field__star'>*</p>
              </span>
              <Select
                field='gender'
                options={gender}
                selectedDef={personalInfo.gender}
                setSelected={(param, value, relatedValue) =>
                  addPersonalInfo(param, value, relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Fecha de nacimiento<p className='field__star'>*</p>
              </span>
              <input
                className='field__input'
                type='date'
                value={personalAge || ''}
                min='1900-01-01'
                max={getMinimumBirthdateLimit(6)}
                onChange={({ target }) => handleBirthdateChange(target.value)}
              />
              <span className='field__text field__text--sub'>Edad: {age} anios</span>
            </div>
          </div>

          <div className='form__section section'>
            <div className='section__field field'>
              <span className='field__text'>
                Estudia actualmente<p className='field__star'>*</p>
              </span>
              <Select
                field='studyCurrently'
                options={diseases}
                selectedDef={personalInfo.studyCurrently}
                setSelected={(param, value, relatedValue) =>
                  addPersonalInfo(param, value, relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Grado o estudio que cursa<p className='field__star'>*</p>
              </span>
              <Select
                field='gradeOrStudy'
                options={studyGrades}
                selectedDef={personalInfo.gradeOrStudy}
                setSelected={(_, __, relatedValue) =>
                  addPersonalInfo('gradeOrStudy', relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Nivel de escolaridad<p className='field__star'>*</p>
              </span>
              <Select
                field='scholarship'
                options={schoolLevel}
                selectedDef={personalInfo.scholarship}
                setSelected={(param, value, relatedValue) =>
                  addPersonalInfo(param, value, relatedValue)
                }></Select>
            </div>
          </div>

          <div className='form__section section'>
            <div className='section__field field'>
              <span className='field__text'>
                Pertenece a la comunidad LGBTIQ+<p className='field__star'>*</p>
              </span>
              <Select
                field='lgbtiq'
                options={diseases}
                selectedDef={personalInfo.lgbtiq}
                setSelected={(param, value, relatedValue) =>
                  addPersonalInfo(param, value, relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Orientacion sexual<p className='field__star'>*</p>
              </span>
              <Select
                field='sexualOrientation'
                options={sexualOrientation}
                selectedDef={personalInfo.sexualOrientation}
                setSelected={(param, value, relatedValue) => {
                  addPersonalInfo(param, value, relatedValue);
                  if (!isOtherValue(relatedValue)) {
                    setSexualOrientationOther('');
                  }
                }}></Select>
            </div>
            {isOtherSelected(
              sexualOrientation,
              personalInfo.sexualOrientation,
              personalInfo.sexualOrientationValue
            ) && (
              <div className='section__field field'>
                <span className='field__text'>Especifique orientacion sexual</span>
                <input
                  className='field__input'
                  type='text'
                  value={sexualOrientationOther}
                  onInput={({ target }) => setSexualOrientationOther(target.value)}
                />
              </div>
            )}
            <div className='section__field field'>
              <span className='field__text'>
                Etnia<p className='field__star'>*</p>
              </span>
              <Select
                field='ethnicGroup'
                options={SCHOOL_ETHNIC_GROUP_OPTIONS}
                selectedDef={personalInfo.ethnicGroup}
                setSelected={(param, value, relatedValue) =>
                  addPersonalInfo(param, value, relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Poblacion victima<p className='field__star'>*</p>
              </span>
              <Select
                field='victimPopulation'
                options={SCHOOL_VICTIM_POPULATION_OPTIONS}
                selectedDef={personalInfo.victimPopulation}
                setSelected={(param, value, relatedValue) =>
                  addPersonalInfo(param, value, relatedValue)
                }></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Se identifica como campesino<p className='field__star'>*</p>
              </span>
              <Select
                field='isFarmer'
                options={diseases}
                selectedDef={personalInfo.isFarmer}
                setSelected={(_, value) => {
                  addPersonalInfo('isFarmer', value);
                  addPersonalInfo('farmer', value === 'Si');
                }}></Select>
            </div>
            <div className='section__field field'>
              <span className='field__text'>
                Enfoque de discapacidad<p className='field__star'>*</p>
              </span>
              <MultiChoice
                field='disability'
                options={disabilities}
                selectedIds={personalInfo.disability}
                selectedValues={personalInfo.disabilityValue}
                onChange={(value, relatedValue) => {
                  addPersonalInfo('disability', value, relatedValue);
                  if (!isOtherSelected(disabilities, value, relatedValue)) {
                    setDisabilityOther('');
                  }
                }}
              />
            </div>
            {isOtherSelected(
              disabilities,
              personalInfo.disability,
              personalInfo.disabilityValue
            ) && (
              <div className='section__field field'>
                <span className='field__text'>Especifique discapacidad</span>
                <input
                  className='field__input'
                  type='text'
                  value={disabilityOther}
                  onInput={({ target }) => setDisabilityOther(target.value)}
                />
              </div>
            )}
            <div className='section__field field'>
              <span className='field__text'>
                Problematicas y factores de riesgo<p className='field__star'>*</p>
              </span>
              <MultiChoice
                field='riskFactor'
                options={riskOptions}
                selectedIds={personalInfo.riskFactor}
                selectedValues={personalInfo.riskFactorValue}
                onChange={(value, relatedValue) => {
                  addPersonalInfo('riskFactor', value, relatedValue);
                  if (!isOtherSelected(riskOptions, value, relatedValue)) {
                    setRiskFactorOther('');
                  }
                }}
              />
            </div>
            {isOtherSelected(
              riskOptions,
              personalInfo.riskFactor,
              personalInfo.riskFactorValue
            ) && (
              <div className='section__field field'>
                <span className='field__text'>Especifique problematica o factor</span>
                <input
                  className='field__input'
                  type='text'
                  value={riskFactorOther}
                  onInput={({ target }) => setRiskFactorOther(target.value)}
                />
              </div>
            )}
            <div className='section__field field'>
              <span className='field__text field__text--optional'>Observaciones</span>
              <textarea
                className='field__input'
                name='observations'
                value={personalInfo.observations}
                rows={4}
                onInput={({ target }) =>
                  addPersonalInfo(target.name, target.value)
                }
              />
            </div>
          </div>

          {showAttendantFields && (
            <div className='form__section section'>
              <div className='section__field field'>
                <span className='field__text'>Nombre del acudiente</span>
                <input
                  className='field__input'
                  name='attendantName'
                  autoComplete='off'
                  value={personalInfo.attendantName}
                  type='text'
                  onInput={({ target }) =>
                    addPersonalInfo(target.name, target.value)
                  }
                />
              </div>
              <div className='section__field field'>
                <span className='field__text'>Correo electronico del acudiente</span>
                <input
                  className='field__input'
                  type='email'
                  name='attendantEmail'
                  placeholder='Ejm: example@example.com'
                  value={personalInfo.attendantEmail}
                  onInput={({ target }) =>
                    addPersonalInfo(target.name, target.value)
                  }
                />
              </div>
              <div className='section__field field'>
                <span className='field__text'>Telefono del acudiente</span>
                <input
                  className='field__input'
                  type='text'
                  name='attendantPhone'
                  value={personalInfo.attendantPhone}
                  onInput={({ target }) =>
                    addPersonalInfo(target.name, target.value)
                  }
                />
              </div>
            </div>
          )}

          {!isPublicAttendanceMode && (
          <div className='form__section section'>
            <div className='section__field field field--consent'>
              <span className='field__text field__text--optional'>
                Consentimiento informado (PDF)
              </span>
              <div className='consent-upload'>
                <label className='consent-upload__picker'>
                  <span className='consent-upload__picker-button'>
                    Seleccionar PDF
                  </span>
                  <span className='consent-upload__picker-text'>
                    {hasSelectedConsent
                      ? 'Ya seleccionaste un archivo nuevo para este beneficiario.'
                      : hasStoredConsent
                        ? 'Puedes revisar o reemplazar el consentimiento actual.'
                        : 'Puedes adjuntar un PDF para dejarlo guardado con la caracterizacion.'}
                  </span>
                  <input
                    className='consent-upload__picker-input'
                    type='file'
                    accept='.pdf,application/pdf'
                    onChange={onConsentFileChange}
                  />
                </label>

                {hasStoredConsent || hasSelectedConsent ? (
                  <div className='consent-upload__list'>
                    {hasStoredConsent && (
                      <article className='consent-upload__card'>
                        <div className='consent-upload__card-header'>
                          <span className='consent-upload__badge'>
                            Archivo cargado
                          </span>
                          <p className='consent-upload__name'>
                            {storedConsentFileName || 'Consentimiento actual'}
                          </p>
                          <p className='consent-upload__meta'>
                            Este es el PDF que ya esta guardado en la plataforma.
                          </p>
                        </div>

                        <div className='consent-upload__actions'>
                          <button
                            type='button'
                            className='consent-upload__action'
                            onClick={handleOpenStoredConsent}>
                            Ver
                          </button>
                          <button
                            type='button'
                            className='consent-upload__action consent-upload__action--primary'
                            onClick={handleDownloadStoredConsent}
                            disabled={isDownloadingStoredConsent}>
                            {isDownloadingStoredConsent
                              ? 'Descargando...'
                              : 'Descargar'}
                          </button>
                        </div>
                      </article>
                    )}

                    {hasSelectedConsent && (
                      <article className='consent-upload__card consent-upload__card--selected'>
                        <div className='consent-upload__card-header'>
                          <span className='consent-upload__badge consent-upload__badge--selected'>
                            Archivo seleccionado
                          </span>
                          <p className='consent-upload__name'>
                            {consentFileName}
                          </p>
                          <p className='consent-upload__meta'>
                            Se reemplazara el consentimiento actual cuando guardes.
                          </p>
                        </div>

                        <div className='consent-upload__actions'>
                          <button
                            type='button'
                            className='consent-upload__action'
                            onClick={handleOpenSelectedConsent}>
                            Ver
                          </button>
                          <button
                            type='button'
                            className='consent-upload__action consent-upload__action--primary'
                            onClick={handleDownloadSelectedConsent}>
                            Descargar
                          </button>
                          <button
                            type='button'
                            className='consent-upload__action consent-upload__action--ghost'
                            onClick={handleClearSelectedConsent}>
                            Quitar
                          </button>
                        </div>
                      </article>
                    )}
                  </div>
                ) : (
                  <div className='consent-upload__empty'>
                    Aun no hay un consentimiento cargado para este beneficiario.
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
            </>
          )}
          <div className='form__terms terms'>
            <label htmlFor='tyc' className='terms__wrapper'>
              <input
                type='checkbox'
                id='tyc'
                onChange={() => setIsCheckedTerms(!isCheckedTerms)}
                checked={isCheckedTerms}
              />
              <span className='terms__checkbox cb'>
                <span className='cb__icon'></span>
              </span>
            </label>
            <span className='terms__text'>
              Acepto las politicas de tratamiento de datos.{' '}
              <a
                href='https://www.medellin.gov.co/normograma/docs/astrea/docs/d_alcamed_1096_2018.htm'
                className='terms__url'>
                Ver terminos y condiciones
              </a>
              <span className='required-indicator' aria-hidden='true'>*</span>
            </span>
          </div>
          {isAttendanceBatchMode && (
            <div className='form__section section section--attendance-batch'>
              <div className='section__field field field--all-row'>
                <span className='field__text'>Sesiones incluidas en la firma</span>
                <p className='attendance-batch__notice' role='status'>
                  Vas a firmar la asistencia de{' '}
                  <strong>
                    {selectedBatchMeetingIds.length}{' '}
                    {selectedBatchMeetingIds.length === 1 ? 'sesion' : 'sesiones'}
                  </strong>.
                  {' '}Estas sesiones fueron definidas por el mediador y no se pueden modificar.
                </p>
                <div className='attendance-batch__sessions'>
                  {sortedBatchMeetings.length > 0 ? (
                    sortedBatchMeetings.map(meeting => {
                      const meetingId = normalizeMeetingId(meeting);
                      return (
                        <div
                          className='attendance-batch__session'
                          key={meetingId}>
                          <span
                            className='material-symbols-rounded attendance-batch__session-icon'
                            aria-hidden='true'>
                            check_circle
                          </span>
                          <span className='attendance-batch__session-name'>
                            {getMeetingLabel(meeting)}
                          </span>
                          <span className='attendance-batch__session-date'>
                            {getMeetingDateLabel(meeting)}
                          </span>
                        </div>
                      );
                    })
                  ) : (
                    <p className='attendance-batch__empty'>
                      No se encontraron sesiones para este grupo.
                    </p>
                  )}
                </div>
              </div>

              <div className='section__field field field--all-row'>
                <span className='field__text'>Firma de asistencia</span>
                <SignatureCanvas
                  ref={signatureRef}
                  className='attendance-batch__signature'
                  height={190}
                  hint='Una sola firma se aplicara a todas las sesiones indicadas.'
                  onDrawChange={setHasBatchSignature}
                />
              </div>
            </div>
          )}
          <div className='form__actions actions'>
            <button
              className='actions__save'
              onClick={handleSaveClick}
              disabled={savingBatchAttendance}>
              {savingBatchAttendance
                ? 'Guardando...'
                : isAttendanceBatchMode
                  ? 'Guardar y firmar asistencia'
                  : edit
                    ? 'Editar'
                    : 'Guardar'}
            </button>
            {!isPublicAttendanceMode && (
              <button className='actions__cancel' onClick={handleCancelClick}>
                Cancelar
              </button>
            )}
          </div>
        </div>
      </div>
      </main>
    </>
  );
}
